// Exports: AddTaskMemberModal
import React, { useMemo, useState } from "react";
import { useAlert } from "components/UI_components/Alert";
import useTaskApi from "hooks/useTaskApi";

const AddTaskMemberModal = ({
  task,
  siteMembers,
  getInitials,
  onClose,
  onMembersAdded,
  preserveScroll,
}) => {
  const { addAlert } = useAlert();
  const { addTaskMembers } = useTaskApi();
  const [selectedMembers, setSelectedMembers] = useState([]);

  const filteredMembers = useMemo(
    () =>
      siteMembers.filter(
        (member) =>
          !task.members.some((taskMember) => taskMember.member_id === member.member_id)
      ),
    [siteMembers, task.members]
  );

  const handleMemberSelect = (member) => {
    setSelectedMembers((current) =>
      current.some((candidate) => candidate.member_id === member.member_id)
        ? current.filter((candidate) => candidate.member_id !== member.member_id)
        : [...current, member]
    );
  };

  const handleAddMembers = async () => {
    const newMembers = selectedMembers.filter(
      (member) =>
        !task.members.some((taskMember) => taskMember.member_id === member.member_id)
    );

    if (newMembers.length === 0) return;

    await preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, async () => {
      try {
        const response = await addTaskMembers({
          task_id: task.id,
          member_ids: newMembers.map((member) => member.member_id),
        });
        const data = await response.json();

        if (data?.detail) {
          addAlert(
            "You do not have permission to add members to task. You must have Task Authority Permission",
            "error"
          );
          return;
        }

        await onMembersAdded(newMembers);
        setSelectedMembers([]);
        onClose();
      } catch (error) {
        console.error("Error adding task members:", error);
      }
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Task Members</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="max-h-60 overflow-y-auto mb-4">
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <div
                key={member.member_id}
                className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-gray-100 ${
                  selectedMembers.some(
                    (candidate) => candidate.member_id === member.member_id
                  )
                    ? "bg-gray-200"
                    : ""
                }`}
                onClick={() => handleMemberSelect(member)}
              >
                <div className="flex items-center">
                  {member?.userpic ? (
                    <img
                      src={member.userpic}
                      alt={`${member.first_name} ${member.last_name}`}
                      className="w-8 h-8 rounded-full border-2 border-white mr-2"
                    />
                  ) : (
                    <span
                      className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm flex items-center justify-center mr-2"
                      title={`${member.first_name} ${member.last_name}`}
                    >
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  )}
                  <span className="text-sm text-gray-700">
                    {member.first_name} {member.last_name}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={selectedMembers.some(
                    (candidate) => candidate.member_id === member.member_id
                  )}
                  onChange={() => handleMemberSelect(member)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
              </div>
            ))
          ) : (
            <div className="text-gray-500 text-sm">No members found to add.</div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            onClick={handleAddMembers}
          >
            Add Members
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskMemberModal;
